import { Popover } from "@mui/material";
import {
  type FC,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";

import { FilterChip } from "~/components/UI/FilterChip";
import { FilterMenuTitle } from "~/components/UI/FilterMenuTitle";
import { isSameFormValue } from "~/components/UI/FilterRange/utils";
import type { IRangeValue } from "~/types/range";
import { formatRange } from "~/utils/formatRange";

import { FilterRangeForm } from "./components/FilterRangeForm";

export interface FilterRangeProps {
  label: string;
  icon?: ReactNode;
  onChange: (value: IRangeValue) => void;
  initialValue?: IRangeValue;
}

export const FilterRange: FC<FilterRangeProps> = ({
  label,
  icon,
  onChange,
  initialValue,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const selectedLabel = useMemo(
    () => formatRange(initialValue),
    [initialValue]
  );

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleApply = useCallback(
    (value: IRangeValue) => {
      if (!isSameFormValue(value, initialValue)) {
        onChange(value);
      }

      handleClose();
    },
    [handleClose, initialValue, onChange]
  );

  const handleClear = useCallback(() => onChange({}), [onChange]);

  return (
    <>
      <FilterChip
        label={label}
        icon={icon}
        valueLabel={selectedLabel || undefined}
        onClick={handleOpen}
        onClear={handleClear}
      />

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <FilterMenuTitle sx={{ px: 2, pt: 2 }}>{label}</FilterMenuTitle>
        <FilterRangeForm
          key={`${open}-${initialValue?.min ?? ""}-${initialValue?.max ?? ""}`}
          initialValue={initialValue}
          onApply={handleApply}
        />
      </Popover>
    </>
  );
};
